/**
 * PlannerPanel.jsx — the Job Planner UI (contract v1.0).
 *
 * CONTROLLED COMPONENT: all durable state (registered SKUs, order lines, the
 * computed plan, sheet/kerf/margin config, busy/error) lives in App.js and is
 * passed in via props. This is deliberate — the panel is conditionally rendered,
 * so keeping state here would lose an in-progress plan whenever the user switches
 * workspace mode and returns. With state lifted, the plan survives unmount.
 *
 * Demand is entered by PICKING from registered SKUs (not typing ids), so cartons
 * registered from the vector flow (or via /extract/detect-die on upload) are
 * directly selectable for planning.
 *
 * Forward-compat (contract §7): `objective`/`require_signoff` are read from the
 * response; the provisional hint shows only when the flag is set; candidates go
 * through normalizeCandidate so the v1.4 multi-form shape is absorbed.
 *
 * Props (all from App.js):
 *   skus            — registered SKU[] (from GET /planner/skus).
 *   orderLines      — current OrderLine[] draft (id, quantity, …).
 *   setOrderLines   — setter.
 *   plan            — last PlanResponse or null.
 *   sheet           — { w, h, gripper } config.
 *   setSheet        — setter.
 *   busy, error, csvWarnings — status.
 *   onAddOrderLine(line), onUploadCsv(file), onComputePlan(), onRefreshSkus(),
 *   onUsePlan(upCounts, candidate) — action callbacks.
 *   knownSkuIds     — string[] of SKU ids that have uploaded artwork (for warnings).
 *   draft* / setDraft* — ephemeral entry fields (kept in App so they persist too).
 */

import React from 'react';
import { normalizeCandidate, overrunBadge, isMultiForm, toleranceSourceLabel, phaseLabel, isPlanTerminal } from './planner';
// PR ⑬a: gate utilization emphasis on prepress_tiled (not prepress_applied).
import { prepressTiled, utilizationCaveat, prepressWarnings } from './prepress';

import { useTheme } from './theme';
import LenInput from './LenInput';

function Btn({ children, onClick, primary = true, disabled = false, small = false }) {
  const { P, FN } = useTheme();
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontFamily: FN, fontWeight: 500, cursor: disabled ? 'default' : 'pointer',
      fontSize: small ? 11 : 12.5, padding: small ? '5px 12px' : '8px 16px', borderRadius: 6, letterSpacing: 0.1,
      border: primary ? 'none' : `0.5px solid ${P.rule}`,
      background: primary ? P.ink : 'transparent', color: primary ? P.sheet : P.muted,
      opacity: disabled ? 0.4 : 1,
    }}>{children}</button>
  );
}

const badgeColor = (P, level) => (level === 'ok' ? P.ok : level === 'warn' ? P.warn : P.danger);
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : n);

/* ---------------- Async job progress (v1.2) ---------------- */

function JobProgress({ job, cancelling, onCancel, onPlanAgain }) {
  const { P, FN, MO } = useTheme();
  if (!job) return null;
  const status = job.status;
  const prog = job.progress || {};
  const phase = prog.phase || status;
  const fraction = typeof prog.fraction === 'number' ? prog.fraction : 0;
  const pct = Math.round(fraction * 100);
  const best = prog.best_so_far ? normalizeCandidate(prog.best_so_far) : null;

  if (status === 'cancelled') {
    // v1.3 §4: neutral state — not an error, not an empty result. result is null
    // by contract; whatever the user had before planning is untouched.
    return (
      <div style={{
        margin: '4px 0 16px', padding: '10px 14px', borderRadius: 8,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        background: P.elev, border: `1px solid ${P.rule}`, fontFamily: FN, fontSize: 12, color: P.muted,
      }}>
        <span>Planning cancelled. No plan was produced — your previous inputs are untouched.</span>
        {onPlanAgain && (
          <button onClick={onPlanAgain} style={{
            fontFamily: MO, fontSize: 11, fontWeight: 500, padding: '4px 12px', borderRadius: 6,
            cursor: 'pointer', background: 'transparent', color: P.ink, border: `1px solid ${P.rule}`,
          }}>Plan again</button>
        )}
      </div>
    );
  }
  if (status === 'failed' || status === 'succeeded') {
    return null; // parent shows job.error / the result
  }

  return (
    <div style={{
      margin: '4px 0 16px', padding: '14px 16px', borderRadius: 8,
      background: P.surface, border: `1px solid ${P.rule}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{
          width: 12, height: 12, borderRadius: '50%', border: `2px solid ${P.rule}`,
          borderTopColor: P.accent, display: 'inline-block', animation: 'mono-spin 0.8s linear infinite',
        }} />
        <span style={{ fontFamily: MO, fontSize: 12, fontWeight: 600, color: P.ink }}>
          {phaseLabel(phase)}
        </span>
        {typeof job.elapsed_seconds === 'number' && (
          <span style={{ fontFamily: MO, fontSize: 10, color: P.dim }}>{job.elapsed_seconds.toFixed(1)}s</span>
        )}
        <button
          onClick={onCancel}
          disabled={cancelling || job.cancel_requested}
          style={{
            marginLeft: 'auto', fontFamily: FN, fontSize: 12, fontWeight: 600, padding: '5px 14px',
            borderRadius: 6, cursor: (cancelling || job.cancel_requested) ? 'default' : 'pointer',
            background: 'transparent', color: P.danger, border: `1px solid ${P.danger}`,
            opacity: (cancelling || job.cancel_requested) ? 0.5 : 1,
          }}
        >{job.cancel_requested ? 'Cancelling…' : 'Cancel'}</button>
      </div>

      {prog.message && (
        <div style={{ fontFamily: MO, fontSize: 11, color: P.muted, marginBottom: 8 }}>{prog.message}</div>
      )}

      <div style={{ height: 6, borderRadius: 3, background: P.bg, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: P.accent, transition: 'width 0.3s ease' }} />
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontFamily: MO, fontSize: 10, color: P.dim }}>
        {prog.candidates_total != null && (
          <span>evaluated {fmt(prog.candidates_evaluated ?? 0)} / {fmt(prog.candidates_total)}</span>
        )}
        {prog.mne_calls != null && <span>nesting calls: {fmt(prog.mne_calls)}</span>}
        {prog.feasible_found != null && <span>feasible: {fmt(prog.feasible_found)}</span>}
      </div>

      {best && (
        <div style={{
          marginTop: 10, padding: '8px 10px', borderRadius: 6, background: P.elev,
          fontFamily: MO, fontSize: 11, color: P.ink, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <span style={{ fontSize: 9, letterSpacing: 0.5, textTransform: 'uppercase', color: P.dim }}>Best so far</span>
          <span>{best.total_up}-up × {fmt(best.sheets)} sheets</span>
          <span
            style={{ color: P.muted }}
            title={utilizationCaveat(best) || undefined}
          >
            {(best.utilization * 100).toFixed(1)}% util{prepressTiled(best) ? ' ⚠' : ''}
          </span>
          <span style={{ color: P.muted }}>
            {Object.entries(best.up_counts).map(([k, v]) => `${k}:${v}`).join('  ')}
          </span>
        </div>
      )}
    </div>
  );
}

/* ---------------- Candidate comparison card (#4) ---------------- */

function Metric({ label, value, sub, accent }) {
  const { P, MO } = useTheme();
  return (
    <div style={{ minWidth: 92 }}>
      <div style={{ fontFamily: MO, fontSize: 9, letterSpacing: 0.5, textTransform: 'uppercase', color: P.dim }}>{label}</div>
      <div style={{ fontFamily: MO, fontSize: 15, fontWeight: 600, color: accent || P.ink, lineHeight: 1.3 }}>{value}</div>
      {sub && <div style={{ fontFamily: MO, fontSize: 9, color: P.muted }}>{sub}</div>}
    </div>
  );
}

function CandidateCard({ candidate, index, requireSignoff, onUse, knownSkuIds, best }) {
  const { P, FN, MO, TC } = useTheme();
  const c = normalizeCandidate(candidate);
  const utilPct = (c.utilization * 100).toFixed(1);
  const multi = isMultiForm(candidate);
  // PR ⑬a: reads the RESPONSE, not the requested mode — `auto` may return plain.
  const tiled = prepressTiled(c);
  const utilCaveat = utilizationCaveat(c);

  // Comparison deltas vs the top pick (so tradeoffs are scannable, #4).
  const bestN = best ? normalizeCandidate(best) : null;
  const dSheets = bestN ? c.sheets - bestN.sheets : 0;
  const dWaste = bestN ? c.total_overrun - bestN.total_overrun : 0;
  const dUtil = bestN ? (c.utilization - bestN.utilization) * 100 : 0;

  const missing = (knownSkuIds && knownSkuIds.length)
    ? Object.keys(c.up_counts).filter((id) => !knownSkuIds.includes(id))
    : Object.keys(c.up_counts);

  // The contract's max_overrun_pct counts only OVER-production, so with the
  // default min_overrun objective it's usually 0 — which hides the under-shipping
  // the plan is actually doing. Compute the worst deviation in EITHER direction
  // across SKUs so the badge is informative regardless of objective.
  const devs = c.placements.map((pl) => pl.overrun_pct || 0);
  const worstUnder = devs.length ? Math.min(...devs) : 0;   // most negative
  const worstOver = devs.length ? Math.max(...devs) : 0;    // most positive
  const worstAbs = Math.max(Math.abs(worstUnder), worstOver);
  const worstSigned = Math.abs(worstUnder) > worstOver ? worstUnder : worstOver;
  const devLevel = overrunBadge(worstAbs);

  return (
    <div style={{
      border: `1px solid ${index === 0 ? P.accent : P.rule}`, borderRadius: 10,
      background: P.surface, padding: 16, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        {index === 0 && (
          <span style={{
            fontFamily: MO, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
            color: P.sheet, background: P.accent, padding: '2px 7px', borderRadius: 4,
          }}>Top pick</span>
        )}
        <span style={{ fontFamily: MO, fontSize: 13, fontWeight: 700, color: P.muted }}>Option {index + 1}</span>
        {multi && <span style={{ fontFamily: MO, fontSize: 9, color: P.muted }}>· {c.forms.length} forms</span>}
        <span style={{
          marginLeft: 'auto', fontFamily: MO, fontSize: 10, fontWeight: 700,
          color: badgeColor(P, devLevel), background: badgeColor(P, devLevel) + '22', padding: '2px 8px', borderRadius: 4,
        }} title="Worst single-SKU deviation from demand across this plan (negative = under, positive = over).">
          {worstAbs < 0.05
            ? 'on target'
            : `worst ${worstSigned > 0 ? '+' : ''}${worstSigned.toFixed(1)}% ${worstSigned < 0 ? 'under' : 'over'}`}
        </span>
      </div>

      {/* Headline metric row (#4) */}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14 }}>
        <Metric label="Up / sheet" value={`${c.total_up}-up`} />
        <Metric label="Sheets to print" value={fmt(c.sheets)} sub={bestN && index !== 0 ? (dSheets === 0 ? 'same' : `${dSheets > 0 ? '+' : ''}${fmt(dSheets)}`) : null} />
        <Metric label={`Utilization${tiled ? ' ⚠' : ''}`} value={`${utilPct}%`} sub={bestN && index !== 0 ? (Math.abs(dUtil) < 0.05 ? 'same' : `${dUtil > 0 ? '+' : ''}${dUtil.toFixed(1)} pts`) : null} accent={(!tiled && c.utilization >= 0.85) ? P.ok : undefined} />
        <Metric label="Overproduction" value={`${fmt(c.total_overrun)}`} sub={bestN && index !== 0 ? (dWaste === 0 ? 'same' : `${dWaste > 0 ? '+' : ''}${fmt(dWaste)}`) : 'units'} accent={c.total_overrun === 0 ? P.ok : c.total_overrun > 0 ? P.warn : undefined} />
      </div>

      {/* Utilization bar. PR ⑬a (D3): the "good" colour is gated on
          prepress_tiled — a tile's enclosed scrap inflates gross utilization,
          so a green bar there would rank a layout on the one number the
          contract says must never rank anything. Plain mode is untouched. */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ height: 6, borderRadius: 3, background: P.bg, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${utilPct}%`, background: (!tiled && c.utilization >= 0.85) ? P.ok : P.accent }} />
        </div>
        {tiled && (
          <div style={{ fontFamily: FN, fontSize: 10, color: P.muted, marginTop: 5, lineHeight: 1.4 }}>
            {utilCaveat}
          </div>
        )}
      </div>

      {/* Per-SKU table (#4) */}
      <div style={{ marginBottom: 14, border: `1px solid ${P.rule}`, borderRadius: 8, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: 8,
          padding: '6px 10px', background: P.elev, fontFamily: MO, fontSize: 9,
          letterSpacing: 0.5, textTransform: 'uppercase', color: P.dim,
        }}>
          <span>SKU</span><span>Up</span><span>Produced</span><span>Demand</span><span>Over/Under</span>
        </div>
        {c.placements.map((pl, i) => {
          const level = overrunBadge(pl.overrun_pct);
          // Effective tolerance + source (brief §3.1). The source is the key:
          // it explains *why* an overrun is what it is (e.g. "using shop default").
          const hasTol = pl.over_tolerance_pct != null || pl.under_tolerance_pct != null;
          const overSrc = pl.over_tolerance_source;
          const underSrc = pl.under_tolerance_source;
          return (
            <div key={pl.sku_id} style={{ borderTop: `1px solid ${P.rule}` }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: 8, alignItems: 'center',
                padding: '6px 10px', fontFamily: MO, fontSize: 11, color: P.ink,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: TC[i % TC.length] }} />
                  {pl.sku_id}
                </span>
                <span style={{ color: P.muted }}>{pl.up_count}</span>
                <span style={{ color: P.muted }}>{fmt(pl.produced)}</span>
                <span style={{ color: P.muted }}>{fmt(pl.demand)}</span>
                <span style={{
                  color: badgeColor(P, level), background: badgeColor(P, level) + '22',
                  padding: '1px 6px', borderRadius: 4, textAlign: 'center', fontWeight: 700,
                }}>
                  {pl.overrun > 0 ? '+' : ''}{fmt(pl.overrun)} ({pl.overrun_pct > 0 ? '+' : ''}{pl.overrun_pct.toFixed(1)}%)
                </span>
              </div>
              {hasTol && (
                <div style={{
                  padding: '0 10px 6px 26px', fontFamily: MO, fontSize: 10, color: P.dim,
                  display: 'flex', gap: 12, flexWrap: 'wrap',
                }}>
                  <span>
                    tolerance: −{pl.under_tolerance_pct ?? '·'}% / +{pl.over_tolerance_pct ?? '·'}%
                  </span>
                  {/* Per contract §6.6: show the source hint ONLY when the user
                      didn't set it — i.e. "config" (shop default) or "moq" (pinned).
                      For "order_line"/"request" the value is expected, so stay quiet.
                      The under-source is the one that drives the "why is overrun −2%?"
                      surprise, so it takes precedence for the hint. */}
                  {(() => {
                    const hintSrc =
                      (underSrc === 'config' || underSrc === 'moq') ? underSrc :
                      (overSrc === 'config') ? overSrc : null;
                    if (!hintSrc) return null;
                    return (
                      <span style={{ color: P.warn }} title="You didn't override this tolerance — it used the value shown.">
                        {toleranceSourceLabel(hintSrc)}
                      </span>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {missing.length > 0 && (
        <div style={{ fontFamily: FN, fontSize: 11, color: P.warn, marginBottom: 10 }}>
          ⚠ No uploaded artwork for: {missing.join(', ')}. You'll need the PDF(s) to render/export this plan.
        </div>
      )}

      <Btn onClick={() => onUse(c.up_counts, candidate)}>
        {requireSignoff ? 'Send for approval →' : 'Use this plan →'}
      </Btn>
    </div>
  );
}

/* ---------------- Main panel (controlled) ---------------- */

export default function PlannerPanel({
  skus = [], orderLines = [], setOrderLines,
  plan, sheet, setSheet,
  busy, error, csvWarnings = [],
  job, cancelling, onCancel,
  onAddOrderLine, onUploadCsv, onComputePlan, onRefreshSkus, onUsePlan,
  knownSkuIds,
  draftSku, setDraftSku, draftQty, setDraftQty, draftDue, setDraftDue,
  draftOverTol, setDraftOverTol, draftUnderTol, setDraftUnderTol,
  draftRequired, setDraftRequired,
}) {
  const { P, FN, MO, TC, inp, unit } = useTheme();
  const skuOptions = skus || [];
  const card = { background: P.surface, border: `0.5px solid ${P.rule}`, borderRadius: 10, padding: 16, marginBottom: 14 };
  const eyebrow = { fontFamily: MO, fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: P.dim };

  const addLine = () => {
    const qty = parseInt(draftQty, 10);
    if (!draftSku || !(qty > 0)) return;
    const line = { sku_id: draftSku, quantity: qty };
    if (draftDue) line.due_date = draftDue;
    // ⑭d / rev 5 §6.1: `required` lives on the ORDER LINE, not the SKU. The
    // same SKU can be firm in one line and flexible in another, and a SKU is
    // durable and mutable — a SKU-level flag would need snapshotting to stay
    // honest for a restored job. Omitted when false so an ordinary line is
    // unchanged on the wire.
    if (draftRequired) line.required = true;
    // Optional per-line tolerances (contract §3.2). Omit when blank to use the
    // shop default; send as numbers when provided.
    const over = parseFloat(draftOverTol);
    const under = parseFloat(draftUnderTol);
    if (Number.isFinite(over)) line.over_tolerance_pct = over;
    if (Number.isFinite(under)) line.under_tolerance_pct = under;
    onAddOrderLine(line);
  };

  const removeLine = (idx) => setOrderLines(orderLines.filter((_, i) => i !== idx));

  return (
    <div>
      {/* Registered SKUs (#2) — pick to plan, no free-typing of ids */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={eyebrow}>
            Registered cartons (SKUs)
          </span>
          <button onClick={onRefreshSkus} style={{
            fontFamily: MO, fontSize: 10, color: P.muted, background: 'transparent',
            border: `1px solid ${P.rule}`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer',
          }}>↻ Refresh</button>
        </div>
        {skuOptions.length > 0 ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {skuOptions.map((s, i) => {
              const selected = draftSku === s.sku_id;
              return (
                <button key={s.sku_id} onClick={() => setDraftSku(s.sku_id)} title={s.name || s.sku_id} style={{
                  fontFamily: MO, fontSize: 11, padding: '5px 10px', borderRadius: 4, cursor: 'pointer',
                  background: selected ? P.accent : P.elev, color: selected ? P.sheet : P.ink,
                  border: `1px solid ${selected ? P.accent : P.rule}`, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: TC[i % TC.length] }} />
                  {s.sku_id}
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ fontFamily: FN, fontSize: 12, color: P.dim, lineHeight: 1.5 }}>
            No SKUs registered yet. Register cartons from the Vector Export mode (they appear here),
            or upload a CSV of order lines below referencing your SKU ids.
          </div>
        )}
      </div>

      {/* Order-line entry */}
      <div style={card}>
        <div style={{ ...eyebrow, marginBottom: 10 }}>
          Demand — order lines
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <label style={{ fontFamily: MO, fontSize: 11, color: P.muted, flex: '1 1 160px' }}>
            SKU {skuOptions.length > 0 ? '(pick above or choose)' : 'id'}
            {skuOptions.length > 0 ? (
              <select value={draftSku} onChange={(e) => setDraftSku(e.target.value)} style={inp}>
                <option value="">— select —</option>
                {skuOptions.map((s) => <option key={s.sku_id} value={s.sku_id}>{s.sku_id}</option>)}
              </select>
            ) : (
              <input value={draftSku} onChange={(e) => setDraftSku(e.target.value)} placeholder="e.g. hepaguard" style={inp} />
            )}
          </label>
          <label style={{ fontFamily: MO, fontSize: 11, color: P.muted, flex: '1 1 120px' }}>
            Quantity
            <input type="number" min="1" value={draftQty} onChange={(e) => setDraftQty(e.target.value)} placeholder="50000" style={inp} />
          </label>
          <label style={{ fontFamily: MO, fontSize: 11, color: P.muted, flex: '1 1 140px' }}>
            Due date (optional)
            <input type="date" value={draftDue} onChange={(e) => setDraftDue(e.target.value)} style={inp} />
          </label>
          <label style={{ fontFamily: MO, fontSize: 11, color: P.muted, flex: '0 1 110px' }}>
            Over tol. %
            <input type="number" min="0" step="0.5" value={draftOverTol}
              onChange={(e) => setDraftOverTol(e.target.value)} placeholder="default" style={inp} />
          </label>
          <label style={{ fontFamily: MO, fontSize: 11, color: P.muted, flex: '0 1 110px' }}>
            Under tol. %
            <input type="number" min="0" step="0.5" value={draftUnderTol}
              onChange={(e) => setDraftUnderTol(e.target.value)} placeholder="default" style={inp} />
          </label>
          {/* ⑭d: a firm line is protected even when doing so costs cartons
              elsewhere, so the run can come back with less capacity than was
              ordered. Per-line, never per-SKU. */}
          <label style={{
            fontFamily: MO, fontSize: 11, color: draftRequired ? P.warn : P.muted,
            display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: '0 0 auto',
          }} title="Protect this line even if it costs cartons elsewhere on the sheet">
            <input type="checkbox" checked={!!draftRequired}
              onChange={(e) => setDraftRequired(e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
            firm
          </label>
          <Btn small onClick={addLine} disabled={busy}>Add line</Btn>
          <label style={{
            fontFamily: FN, fontSize: 11, color: P.muted, cursor: 'pointer',
            padding: '6px 12px', borderRadius: 6, border: `1px dashed ${P.rule}`,
          }}>
            ↑ Upload CSV
            <input type="file" accept=".csv,text/csv" style={{ display: 'none' }}
              onChange={(e) => { onUploadCsv(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        </div>

        {csvWarnings.length > 0 && (
          <div style={{ fontFamily: FN, fontSize: 11, color: P.warn, marginBottom: 10, lineHeight: 1.5 }}>
            {csvWarnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
          </div>
        )}

        {orderLines.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {orderLines.map((ol, i) => (
              <div key={ol.line_id || i} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
                borderRadius: 5, background: P.elev, fontFamily: MO, fontSize: 12, color: P.ink,
              }}>
                <span style={{ fontWeight: 600 }}>{ol.sku_id}</span>
                <span style={{ color: P.muted }}>{fmt(ol.quantity)} units</span>
                {(ol.over_tolerance_pct != null || ol.under_tolerance_pct != null) && (
                  <span style={{ color: P.dim, fontSize: 10 }}>
                    tol +{ol.over_tolerance_pct ?? '·'}/−{ol.under_tolerance_pct ?? '·'}%
                  </span>
                )}
                {ol.due_date && <span style={{ color: P.dim }}>due {ol.due_date}</span>}
                {ol.required && (
                  <span style={{
                    color: P.warn, fontSize: 9.5, padding: '1px 6px', borderRadius: 3,
                    border: `0.5px solid ${P.warn}`,
                  }}>firm</span>
                )}
                {/* Stored lines (with a line_id) are referenced by id on /plan and
                    bring their saved tolerances; inline-only lines are sent verbatim. */}
                <span style={{ color: P.dim, fontSize: 9, fontStyle: 'italic' }}>
                  {ol.line_id ? 'stored' : 'inline'}
                </span>
                <button onClick={() => removeLine(i)} style={{
                  marginLeft: 'auto', background: 'none', border: 'none', color: P.dim, cursor: 'pointer', fontSize: 15,
                }}>×</button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontFamily: FN, fontSize: 12, color: P.dim }}>No order lines yet. Pick a SKU and add a quantity, or upload a CSV.</div>
        )}
      </div>

      {/* Sheet config + run */}
      <div style={card}>
        <div style={{ ...eyebrow, marginBottom: 12 }}>Sheet &amp; run</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ fontFamily: MO, fontSize: 11, color: P.muted }}>
            Sheet W ({unit})<LenInput mm={Number(sheet.w) || 0} onMm={(v) => setSheet({ ...sheet, w: String(v) })} stepMm={1} style={inp} />
          </label>
          <label style={{ fontFamily: MO, fontSize: 11, color: P.muted }}>
            Sheet H ({unit})<LenInput mm={Number(sheet.h) || 0} onMm={(v) => setSheet({ ...sheet, h: String(v) })} stepMm={1} style={inp} />
          </label>
          <label style={{ fontFamily: MO, fontSize: 11, color: P.muted }}>
            Gripper / margin ({unit})<LenInput mm={Number(sheet.gripper) || 0} onMm={(v) => setSheet({ ...sheet, gripper: String(v) })} stepMm={1} style={inp} />
          </label>
          <div style={{ marginLeft: 'auto' }}>
            <Btn onClick={onComputePlan} disabled={busy || orderLines.length === 0}>
              {busy ? 'Planning…' : 'Compute plans'}
            </Btn>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          marginBottom: 14, padding: '12px 14px', borderRadius: 8,
          background: 'rgba(239,68,68,0.1)', border: `1px solid ${P.danger}`,
          fontFamily: FN, fontSize: 12, color: P.ink, whiteSpace: 'pre-line',
        }}>{error}</div>
      )}

      {/* Live progress while an async plan job runs (v1.2). Hidden once the job
          succeeds (the plan renders below) or fails (error box above). */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <JobProgress job={job} cancelling={cancelling} onCancel={onCancel} onPlanAgain={onComputePlan} />

      {/* Plan results. While a new job is running the PREVIOUS plan stays visible
          (dimmed) — a cancel or failure leaves it untouched (v1.3 §4). */}
      {plan && (
        <div style={job && !isPlanTerminal(job.status) ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
          {job && !isPlanTerminal(job.status) && (
            <div style={{ fontFamily: MO, fontSize: 10, color: P.dim, marginBottom: 8 }}>
              Showing your previous plan while the new one computes.
            </div>
          )}
          {/* ⑭f / rev 7 §7: a firm line whose SKU is not on `auto` is inert —
              the planner warns rather than rejecting, and until rev 7 that
              warning went to a log line the operator never saw. A flag that
              silently does nothing is the exact failure rev 5 §4 exists to
              prevent, so it is shown on the result, not buried in Details.
              Message VERBATIM: it names the piece and the reason together. */}
          {prepressWarnings(plan).length > 0 && (
            <div style={{
              padding: '10px 12px', borderRadius: 6, marginBottom: 12,
              background: P.warnSoft, border: `0.5px solid ${P.warn}`,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ fontFamily: MO, fontSize: 10, color: P.warn, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Prepress warnings
              </div>
              {prepressWarnings(plan).map((w, i) => (
                <div key={w.piece || i} style={{ fontFamily: FN, fontSize: 11, color: P.ink, lineHeight: 1.5 }}>
                  {w.message}
                </div>
              ))}
            </div>
          )}

          {plan.objective_is_provisional && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontFamily: FN, fontSize: 11, color: P.muted }}>
              <span style={{
                width: 16, height: 16, borderRadius: '50%', background: P.elev, color: P.muted,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
              }}>i</span>
              Ranking preference is being finalised{plan.objective ? ` (currently: ${plan.objective})` : ''}.
            </div>
          )}

          {plan.feasible && plan.candidates?.length > 1 && (
            <div style={{ fontFamily: FN, fontSize: 11, color: P.muted, marginBottom: 10 }}>
              {plan.candidates.length} options ranked best-first. Metrics under each show the tradeoff vs the top pick.
            </div>
          )}

          {!plan.feasible && (
            <div style={{
              padding: '14px 16px', borderRadius: 8, background: P.surface, border: `1px solid ${P.warn}`,
              fontFamily: FN, fontSize: 13, color: P.ink,
            }}>
              <strong style={{ color: P.warn }}>No feasible plan.</strong>
              <div style={{ marginTop: 8, lineHeight: 1.6 }}>
                {(plan.notes || []).map((n, i) => <div key={i}>{n}</div>)}
              </div>
            </div>
          )}

          {plan.feasible && plan.candidates?.map((cand, i) => (
            <CandidateCard
              key={i}
              candidate={cand}
              index={i}
              best={plan.candidates[0]}
              requireSignoff={plan.require_signoff}
              onUse={onUsePlan}
              knownSkuIds={knownSkuIds}
            />
          ))}

          {plan.feasible && plan.notes?.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ fontFamily: MO, fontSize: 11, color: P.muted, cursor: 'pointer' }}>Details</summary>
              <div style={{ fontFamily: FN, fontSize: 11, color: P.dim, marginTop: 6, lineHeight: 1.6 }}>
                {plan.notes.map((n, i) => <div key={i}>{n}</div>)}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
